import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { formatDateForAPI } from "../Utility/const";
import { getRajasthanGssRestrictedAreaSummaryMetrics,
  getRajasthanGssRestrictedSummaryMetricsDiscom,
  getRajasthanGssLoadMetricsRestrictedDiscomDistrict,
} from "../Utility/RestApi";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { getRestrictedLoadMetrics } from "../Utility/RestApi";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  { label: "Area", key: "area", values: ["station", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "discom"] },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL", apiKey: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL", apiKey: "JVVNL" },
  { label: "AVVNL", key: "AVVNL", apiKey: "AVVNL" },
];

function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((k) => DISCOM.find((d) => d.key === k)?.apiKey ?? k);
}

function getDisplayDiscomName(apiKey: string): string {
  return DISCOM.find((d) => d.apiKey === apiKey)?.label ?? apiKey;
}

// Build a stable station key: keep all parts except the last as station name
function parseStationKey(key: string): { station: string; discom: string } {
  const parts = key.split("_");
  const discom = parts[parts.length - 1];
  const station = parts.slice(0, -1).join("_");
  return { station, discom };
}

interface RajasthanDrawalSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
  selectedArea: any[];
  onAreaChange: (v: any) => void;
  selectedDistricts: any[];
  onDistrictsChange: (v: any) => void;
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanDrawalSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedArea,
  onAreaChange,
  selectedDistricts,
  onDistrictsChange,
  metrics,
}: RajasthanDrawalSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);
  const [areaSummaryData, setAreaSummaryData] = useState<any[]>([]);
  const [discomSummaryData, setDiscomSummaryData] = useState<any[]>([]);
  const [districtSummaryData, setDistrictSummaryData] = useState<any[]>([]);

  const [availableAreas, setAvailableAreas] = useState<string[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;

      if (!dates?.startDate || !dates?.endDate) {
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const [areaRes, discomRes, districtRes] = await Promise.all([
        getRajasthanGssRestrictedAreaSummaryMetrics(startDateStr, endDateStr),
        getRajasthanGssRestrictedSummaryMetricsDiscom(startDateStr, endDateStr),
        getRajasthanGssLoadMetricsRestrictedDiscomDistrict(
          startDateStr,
          endDateStr,
        ),
      ]);

      setAreaSummaryData(Array.isArray(areaRes) ? areaRes : []);
      setDiscomSummaryData(Array.isArray(discomRes) ? discomRes : []);
      setDistrictSummaryData(Array.isArray(districtRes) ? districtRes : []);
    } catch (err) {
      setAreaSummaryData([]);
      setDiscomSummaryData([]);
      setDistrictSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ─── Process ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const groupKey = selectedGroupKeys[0]?.key ?? "area";
    let currentData: any[] = [];
    if (groupKey === "district") {
      currentData = districtSummaryData;
    } else if (groupKey === "discom") {
      currentData = discomSummaryData;
    } else {
      currentData = areaSummaryData;
    }

    if (!currentData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setAvailableAreas([]);
      setAvailableDistricts([]);
      return;
    }

    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    let filtered = [...currentData];

    // 1. Discom filter
    if (selectedDiscom.length > 0) {
      const apiKeys = getApiDiscomKeys(selectedDiscom);
      filtered = filtered.filter((item) => apiKeys.includes(item.discom));
    }

    // 2. Block filter
    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((o) => o.value);
      filtered = filtered.filter((item) => blockValues.includes(item.slot));
    }

    // ── Populate district options ──────────────────────────────────────────
    const districtOptions = Array.from(
      new Set(
        filtered.map((item) => {
          const name =
            item.district || item.divisional_headquarter || item.discom;
          const discomLabel = getDisplayDiscomName(item.discom);
          return `${name} (${discomLabel})`;
        }),
      ),
    )
      .filter((n) => n && !n.includes("undefined") && !n.includes("null"))
      .sort();
    setAvailableDistricts(districtOptions);

    // 3. District filter
    if (selectedDistricts.length > 0) {
      const distStrings = selectedDistricts.map((d: any) => d.value);
      filtered = filtered.filter((item) => {
        const name =
          item.district || item.divisional_headquarter || item.discom;
        const discomLabel = getDisplayDiscomName(item.discom);
        return distStrings.includes(`${name} (${discomLabel})`);
      });
    }

    // ── Populate area options ──────────────────────────────────────────────
    const areaOptions = Array.from(
      new Set(
        filtered
          .map((item) => {
            if (!item.station || item.station === "UNKNOWN") return "";
            return `${item.station} (${getDisplayDiscomName(item.discom)})`;
          })
          .filter((n) => n && !n.includes("undefined") && !n.includes("null")),
      ),
    ).sort();
    setAvailableAreas(areaOptions);

    // 4. Area filter
    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      filtered = filtered.filter((item) => {
        if (!item.station) return false;
        return areaStrings.includes(
          `${item.station} (${getDisplayDiscomName(item.discom)})`,
        );
      });
    }

    if (!filtered.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // ── Build slot map helper ──────────────────────────────────────────────
    const buildSlotMap = (
      data: any[],
      getKey: (item: any) => string,
    ): { slotMap: Map<number, any>; uniqueKeys: string[] } => {
      const slotMap = new Map<number, any>();
      const uniqueKeys = new Set<string>();

      data.forEach((item) => {
        const slot = item.slot;
        const key = getKey(item);
        const cleanKey = key.replace(/ /g, "_");
        uniqueKeys.add(cleanKey);

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }

        const row = slotMap.get(slot)!;
        row[`${cleanKey}_min_load`] = item.min_load * factor;
        row[`${cleanKey}_max_load`] = item.max_load * factor;
        row[`${cleanKey}_avg_load`] = item.avg_load * factor;
        row[`${cleanKey}_mode_load`] = item.mode_load * factor;

        // Store both variants to support tooltip keys used elsewhere in the codebase.
        // Some API endpoints return `date_of_min_load`/`date_of_max_load`,
        // while others might return `date_of_min`/`date_of_max`.
        const dateOfMin = item.date_of_min_load ?? item.date_of_min;
        const dateOfMax = item.date_of_max_load ?? item.date_of_max;

        row[`${cleanKey}_date_of_min`] = dateOfMin;
        row[`${cleanKey}_date_of_min_load`] = dateOfMin;
        row[`${cleanKey}_date_of_max`] = dateOfMax;
        row[`${cleanKey}_date_of_max_load`] = dateOfMax;
      });

      return { slotMap, uniqueKeys: Array.from(uniqueKeys).sort() };
    };

    // ── Group: Area ────────────────────────────────────────────────────────
    if (groupKey === "area") {
      const { slotMap, uniqueKeys } = buildSlotMap(
        filtered,
        (item) => `${item.station ?? "UNKNOWN"}_${item.discom}`,
      );
      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(uniqueKeys);

      // ── Group: Discom ──────────────────────────────────────────────────────
    } else if (groupKey === "discom") {
      const { slotMap, uniqueKeys } = buildSlotMap(
        filtered,
        (item) => item.discom,
      );
      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(uniqueKeys);

      // ── Group: District ────────────────────────────────────────────────────
    } else if (groupKey === "district") {
      const { slotMap, uniqueKeys } = buildSlotMap(filtered, (item) => {
        const discomLabel = getDisplayDiscomName(item.discom);
        const district =
          item.district || item.divisional_headquarter || discomLabel;
        return `${district}_${discomLabel}`;
      });
      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(uniqueKeys);
    }
  }, [
    areaSummaryData,
    discomSummaryData,
    districtSummaryData,
    selectedUnit,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
    selectedDistricts,
    selectedBlock,
  ]);

  // ─── Legends ──────────────────────────────────────────────────────────────
  const legends = useMemo(() => {
    const metricTypes = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];

    const groupKey = selectedGroupKeys[0]?.key ?? "area";
    const validKeys = chartKeys.filter((k) => k?.trim());

    if (!validKeys.length) return [];

    return validKeys.flatMap((key) => {
      let baseName = key;

      if (groupKey === "district") {
        // key = "DISTRICT_NAME_DISCOMLABEL" — last segment is discom, rest is district
        const parts = key.split("_");
        const discomPart = parts[parts.length - 1];
        const districtPart = parts.slice(0, -1).join(" ");
        baseName = `${districtPart} (${discomPart})`;
      } else if (groupKey === "discom") {
        baseName = getDisplayDiscomName(key);
      } else if (groupKey === "area") {
        // key = "STATIONNAME_DISCOMKEY"  (last segment is discom)
        const { station, discom } = parseStationKey(key);
        baseName = `${station} (${getDisplayDiscomName(discom)})`;
      }

      const cleanKey = key.replace(/ /g, "_");
      return metricTypes.map((metric) => ({
        dataKey: `${cleanKey}_${metric.field}_load`,
        name: `${baseName} ${metric.displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: metric.hasDate
          ? `${cleanKey}_date_of_${metric.field}`
          : undefined,
      }));
    });
  }, [chartKeys, selectedGroupKeys, selectedChartType]);

  // ─── Tooltip ──────────────────────────────────────────────────────────────
  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      const matchingLegend = legends.find((leg) => leg.name === seriesName);
      const dataKey = matchingLegend?.dataKey || "";
      const isMin = dataKey.endsWith("_min_load");
      const isMax = dataKey.endsWith("_max_load");
      const keyPrefix = dataKey
        .replace(/_min_load$/, "")
        .replace(/_max_load$/, "")
        .replace(/_avg_load$/, "")
        .replace(/_mode_load$/, "");

      const dateOfMin =
        dataPoint?.[`${keyPrefix}_date_of_min`] ??
        dataPoint?.[`${keyPrefix}_date_of_min_load`];
      const dateOfMax =
        dataPoint?.[`${keyPrefix}_date_of_max`] ??
        dataPoint?.[`${keyPrefix}_date_of_max_load`];

      if (isMin && dateOfMin) {
        return `${seriesName} (${dateOfMin})`;
      } else if (isMax && dateOfMax) {
        return `${seriesName} (${dateOfMax})`;
      }
      return seriesName;
    },
    [legends],
  );

  // ─── Table helpers ────────────────────────────────────────────────────────
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((i) => i.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={(item) => onDiscomChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((i) => i.label)}
            selectedItems={selectedGroupKeys.map((i) => i.label)}
            onSelect={(item, index) => {
              onGroupByChange(index);
              onAreaChange([]);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Restricted Demand Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  {selectedGroupKeys[0]?.key === "district" && (
                    <div className="min-w-60">
                      <SearchBox
                        options={availableDistricts.map((i) => ({
                          value: i,
                          label: i,
                        }))}
                        isMany
                        placeholder="Search District"
                        selectedOptions={selectedDistricts}
                        onChange={(v: any) => {
                          onDistrictsChange(v);
                          onAreaChange([]);
                        }}
                      />
                    </div>
                  )}
                  {(selectedGroupKeys[0]?.key === "area" ||
                    (selectedGroupKeys[0]?.key === "district" &&
                      selectedDistricts.length > 0)) && (
                    <div className="min-w-60">
                      <SearchBox
                        options={availableAreas.map((i) => ({
                          value: i,
                          label: i,
                        }))}
                        isMany
                        placeholder="Search Area"
                        selectedOptions={selectedArea}
                        onChange={(v: any) => onAreaChange(v)}
                      />
                    </div>
                  )}
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((i) => i.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {chartData.length === 0 && !loading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available...
                </div>
              )}

              {dataTab.label === "Chart" && chartData.length > 0 && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={true}
                  onlyTitle
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  legends={legends.map((leg) => ({
                    ...leg,
                    type: selectedChartType.value,
                    stackId: selectedChartType.stackId ?? undefined,
                  }))}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  resolveDisplayName={resolveTooltipName}
                />
              )}

              {dataTab.label === "Summary" && chartData.length > 0 && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && chartData.length > 0 && (
                <ChartTable {...tablePar} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand)
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
